import { useMemo, type ReactNode } from 'react';
import { Checkbox } from './Checkbox';

export interface ChecklistItem {
  value: string;
  label: ReactNode;
  description?: ReactNode;
  meta?: ReactNode;
  required?: boolean;
  disabled?: boolean;
}

export interface ChecklistProps {
  items: ChecklistItem[];
  values: string[] | ReadonlySet<string>;
  onToggle: (value: string, checked: boolean) => void;
  idPrefix?: string;
  showSelectAll?: boolean;
  selectAllLabel?: string;
  disabled?: boolean;
  error?: ReactNode;
  summary?: ReactNode;
  className?: string;
}

export function Checklist({
  items,
  values,
  onToggle,
  idPrefix = 'checklist',
  showSelectAll = false,
  selectAllLabel = 'Select all',
  disabled = false,
  error,
  summary,
  className = '',
}: ChecklistProps) {
  const selected = useMemo(() => new Set(values), [values]);
  const has = (value: string) => selected.has(value);

  const selectable = items.filter((item) => !item.disabled);
  const selectedCount = selectable.filter((item) => has(item.value)).length;
  const allSelected = selectable.length > 0 && selectedCount === selectable.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const toggleAll = (checked: boolean) => {
    for (const item of selectable) {
      if (has(item.value) !== checked) onToggle(item.value, checked);
    }
  };

  return (
    <div className={`ui-checklist${className ? ` ${className}` : ''}`}>
      {showSelectAll && (
        <div className="ui-checklistHeader">
          <Checkbox
            id={`${idPrefix}-select-all`}
            label={selectAllLabel}
            checked={allSelected}
            indeterminate={someSelected}
            disabled={disabled || selectable.length === 0}
            onChange={(event) => toggleAll(event.target.checked)}
          />
          <span className="ui-checklistSummary">{summary ?? `${selectedCount}/${selectable.length}`}</span>
        </div>
      )}
      <ul className="ui-checklistList">
        {items.map((item, index) => (
          <li key={item.value} className="ui-checklistItem">
            <div className="ui-checklistRow">
              <Checkbox
                id={`${idPrefix}-${index}`}
                label={item.label}
                description={item.description}
                checked={has(item.value)}
                disabled={disabled || item.disabled}
                onChange={(event) => onToggle(item.value, event.target.checked)}
              />
              {item.required && <span className="ui-checklistRequired">Required</span>}
            </div>
            {item.meta && <div className="ui-checklistMeta">{item.meta}</div>}
          </li>
        ))}
      </ul>
      {error && (
        <p className="ui-checklistError" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
